import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Card } from 'components/Card';
import { Icon } from 'components/Icon';
import { IconTypes } from 'components/Icon/IconTypes';
import { H3 } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { Direction } from 'design/types';

const Layout = styled(Card)`
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(theme.space.sm)};
  align-self: stretch;
  border-radius: ${toRem(12)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background: ${theme.color.neutral[100]};
  min-height: unset;
  padding: ${toRem(theme.space.lg)};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  align-self: stretch;

  h3 {
    font-size: ${toRem(theme.font.size.lg)};
    font-style: normal;
    font-weight: ${theme.font.weight.semiBold};
    line-height: ${lineHeight(theme.font.size.lg)};
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const IconWrapper = styled.div<{ iconHaloColor?: string }>`
  display: flex;
  /* Hold the halo at 32px; without this a long title crowds it out of round (KICK-5134). */
  flex-grow: 0;
  flex-shrink: 0;
  width: ${toRem(32)};
  height: ${toRem(32)};
  background-color: ${({ iconHaloColor }) => iconHaloColor || theme.color.neutral[200]};
  justify-content: center;
  align-items: center;
  border-radius: ${toRem(16)};

  /* Safari shaves the edge off artwork that fills its viewBox, flattening curves; lifting the svg clip keeps the whole shape (KICK-5134). */
  svg {
    overflow: visible;
  }
`;

const ChildrenContainer = styled.div<{ direction: Direction }>`
  display: flex;
  flex-direction: ${({ direction }) => direction};
  gap: ${toRem(theme.space.sm)};
  width: 100%;

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    flex-wrap: wrap;
  }
`;

interface ICardContainerProps {
  direction?: Direction;
  iconType?: IconTypes;
  iconFill?: string;
  iconHaloColor?: string;
  title?: string;
  children?: ReactNode;
  dataCy?: string;
}

export const CardContainer = ({ direction = Direction.COLUMN, iconType, iconFill, iconHaloColor, title, children, dataCy = 'card-container' }: ICardContainerProps) => (
  <Layout data-cy={dataCy}>
    {(iconType || title) && (
      <Header data-cy={`${dataCy}-header`}>
        {iconType && (
          <IconWrapper iconHaloColor={iconHaloColor} data-cy={`${dataCy}-icon`}>
            <Icon icon={iconType} fill={iconFill || theme.color.neutral[600]} />
          </IconWrapper>
        )}
        {title && <H3>{title}</H3>}
      </Header>
    )}
    <ChildrenContainer direction={direction}>{children}</ChildrenContainer>
  </Layout>
);
